import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { SchemaElement, SchemaRegion } from "../../../api/restaurantSchema";
import SchemaStage, { isFloorTap } from "../../../components/restaurant-schema/SchemaStage";
import type { TableStatus } from "../../../components/restaurant-schema/tableTones";
import type { SchemaStageState } from "../../../components/restaurant-schema/useSchemaStage";
import type { FloorSnapshot } from "../logic/tableStatus";
import { hasNoTables } from "../logic/schemaSetup";
import { tableLinkGroupsOf, type TableLinkGroup } from "../logic/tableLinks";
import { useReservationFormat } from "../hooks/useReservationFormat";
import { floorTableStatus, floorTableTitle } from "./floorTableView";
import { SchemaSetupNotice } from "./PageNotice";
import TablePopover, { type LinkedTable } from "./popover/TablePopover";
import { keepVisibleTableIdsOf } from "./popover/popoverPlacement";
import { TableLinks } from "./TableLinks";
import type { PopoverHandlers } from "./popover/TablePopoverBody";

/**
 * Режимът „избор на маса" (Нова резервация, `useTablePicker`): свои статуси и клик,
 * без поповър. Когато е подаден, заменя статусите в момента T.
 */
export interface FloorPicker {
  tableStatus: (table: SchemaElement) => TableStatus;
  tableTitle?: (table: SchemaElement) => string | undefined;
  onTableClick: (table: SchemaElement) => void;
}

const asFloorTable = (table: SchemaElement) => ({ id: table.id, name: table.name ?? "" });

/**
 * Схемата на региона със статусите на масите в момента T (споделената сцена
 * `SchemaStage`): тон/надпис/чип/точка на всяка маса, hover title, клик →
 * поповър, закотвен до масата. Подчертаните маси (избраната резервация,
 * отвореният поповър) получават ореол.
 *
 * Без `popoverHandlers` („Маркиране" → Схема) — без поповър: кликът само
 * избира, двойният клик отваря (`onTableDoubleClick`), а докосването на
 * празния под маха избора (`onFloorTap`).
 */
const FloorCanvas = ({
  stage,
  region,
  snapshot,
  highlightedTableIds,
  openTableId,
  isPending,
  isNarrow,
  popoverHandlers,
  picker,
  canLinkToSchema,
  onTableClick,
  onTableDoubleClick,
  onClosePopover,
  onFloorTap,
  decorateStatus,
}: {
  stage: SchemaStageState;
  region: SchemaRegion;
  snapshot: FloorSnapshot;
  highlightedTableIds: ReadonlySet<number>;
  openTableId: number | null;
  // Резервациите на деня още се зареждат: масите са неутрални, без поповър.
  isPending: boolean;
  isNarrow: boolean;
  // Няма ли ги — без поповър (`openTableId` не се ползва).
  popoverHandlers?: PopoverHandlers;
  picker: FloorPicker | null;
  // Празен район води към „Схема на заведението" (право за нея и не срещу касата).
  canLinkToSchema: boolean;
  onTableClick: (table: SchemaElement) => void;
  onTableDoubleClick?: (table: SchemaElement) => void;
  onClosePopover?: () => void;
  // Докосване на празния под (не на маса/обект) — без поповър.
  onFloorTap?: () => void;
  // Допълва статуса на масата („Маркиране": масата, чиято сметка се закрива —
  // приглушена, неактивна, „Закрива се").
  decorateStatus?: (table: SchemaElement, status: TableStatus) => TableStatus;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const { momentMs, nowMs, isAtNow } = snapshot;

  const openTable = picker || isPending || !popoverHandlers
    ? undefined
    : region.elements.find((element) => element.id === openTableId && element.kind === "table");

  const baseStatusOf = (table: SchemaElement): TableStatus =>
    picker
      ? picker.tableStatus(table)
      : isPending
        ? { tone: "free", disabled: true }
        : floorTableStatus(
          snapshot.stateOf(asFloorTable(table)),
          momentMs,
          highlightedTableIds.has(table.id) || table.id === openTableId,
          format,
        );

  const statusOf = (table: SchemaElement): TableStatus => {
    const status = baseStatusOf(table);
    return decorateStatus && !picker && !isPending ? decorateStatus(table, status) : status;
  };

  const titleOf = (element: SchemaElement): string | undefined => {
    if (element.kind !== "table") return undefined;
    if (picker?.tableTitle) return picker.tableTitle(element);
    if (isPending) return undefined;
    return floorTableTitle(element, snapshot.stateOf(asFloorTable(element)), momentMs, format, t);
  };

  // Масите на една резервация се свързват постоянно — всяка резервация на
  // няколко маси, която масите показват в момента (настанена, резервирана
  // или часът ѝ в чипа над свободните), — и следват линията при местене;
  // избраната от списъка/клик е силната връзка. Пресмята се само при нов
  // момент/регион/избор — плъзгането остава гладко. В режим „избор на маса"
  // — без връзки.
  const isPicking = picker !== null;
  const linkGroups = useMemo((): TableLinkGroup<SchemaElement>[] => {
    if (isPicking || isPending) return [];
    return tableLinkGroupsOf({
      tables: region.elements.filter((element) => element.kind === "table"),
      stateOf: (table) => snapshot.stateOf(asFloorTable(table)),
      selectedTableIds: highlightedTableIds,
    });
  }, [isPicking, isPending, region, snapshot, highlightedTableIds]);

  // Другите маси на резервацията в поповъра (и на избраната) — поповърът
  // застава така, че да не ги покрива (и чипа „следваща резервация" над
  // свободната — `floorTableStatus`); чужди маси може да покрие.
  const popoverLinkedTables = useMemo((): LinkedTable[] => {
    if (!openTable) return [];
    const keepVisibleIds = keepVisibleTableIdsOf(
      openTable.id,
      snapshot.stateOf(asFloorTable(openTable)),
      highlightedTableIds,
    );
    return region.elements
      .filter((element) => element.kind === "table" && keepVisibleIds.has(element.id))
      .map((table) => {
        const state = snapshot.stateOf(asFloorTable(table));
        return { table, hasChip: state.kind === "free" && state.next !== null };
      });
  }, [openTable, region, snapshot, highlightedTableIds]);

  // Район без маси: с право — пътят към схемата, НАД платното (слоят с
  // елементите се мести и не пуска клика до бутона); в режим „избор на маса"
  // формата е отворена и не се напуска — само текстът, както и без право.
  const schemaLink =
    canLinkToSchema && !picker && hasNoTables(region) ? (
      <div className={`absolute inset-0 flex ${region.elements.length > 0 ? "bg-surface/85" : ""}`}>
        <SchemaSetupNotice scope="region" regionId={region.id} />
      </div>
    ) : null;

  const emptyState: ReactNode = schemaLink ? null : (
    <div className="absolute inset-0 flex items-center justify-center text-[13px] font-medium text-muted">
      {t("reservations.emptyRegion")}
    </div>
  );

  return (
    <SchemaStage
      stage={stage}
      region={region}
      framed={false}
      // На тесен екран легендата няма място за зуума → плаващ в платното.
      zoomControls={isNarrow ? "auto" : "none"}
      tableStatus={statusOf}
      elementTitle={titleOf}
      onTableClick={picker ? picker.onTableClick : onTableClick}
      onTableDoubleClick={picker ? undefined : onTableDoubleClick}
      onCanvasTap={
        picker
          ? undefined
          : popoverHandlers
            ? onClosePopover
            : onFloorTap && ((event) => {
                if (isFloorTap(event)) onFloorTap();
              })
      }
      emptyState={emptyState}
      renderUnderlay={(layout) => <TableLinks layout={layout} groups={linkGroups} />}
      renderOverlay={(layout) =>
        openTable && popoverHandlers ? (
          <TablePopover
            table={openTable}
            linkedTables={popoverLinkedTables}
            regionName={region.name}
            layout={layout}
            state={snapshot.stateOf(asFloorTable(openTable))}
            momentMs={momentMs}
            nowMs={nowMs}
            isAtNow={isAtNow}
            handlers={popoverHandlers}
            onClose={() => onClosePopover?.()}
          />
        ) : null
      }
    >
      {schemaLink}
    </SchemaStage>
  );
};

export default FloorCanvas;
